import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Loader, Textarea } from "@/components/ui/Primitives";
import { Star, ThumbsUp, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function LeaveReview() {
  const { user } = useAuth();
  const isTenant = user?.role === "tenant";
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [target, setTarget] = useState(null); // { user_id, role }
  const [googleUrl, setGoogleUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [leasesRes, contactsRes, googleRes] = await Promise.all([
          api.get("/leases").catch(() => ({ data: [] })),
          api.get("/me/chat-contacts").catch(() => ({ data: [] })),
          api.get("/reviews/google-url").catch(() => ({ data: {} })),
        ]);
        const leases = Array.isArray(leasesRes.data) ? leasesRes.data : leasesRes.data?.leases || [];
        const contacts = Array.isArray(contactsRes.data) ? contactsRes.data : contactsRes.data?.contacts || [];
        setGoogleUrl(googleRes.data?.url || "");
        const lease = leases[0];
        if (lease) {
          setTarget(isTenant
            ? { user_id: lease.landlord_id, role: "landlord" }
            : { user_id: lease.tenant_id, role: "tenant" });
        } else if (contacts[0]?.user_id) {
          setTarget({ user_id: contacts[0].user_id, role: isTenant ? "landlord" : "tenant" });
        }
      } finally { setLoading(false); }
    })();
  }, [isTenant]);

  const submit = async (e) => {
    e.preventDefault();
    if (rating === 0) return toast.error("Pick a rating first");
    setSubmitting(true);
    try {
      // External reviews API needs a body of 10+ chars — pad short comments.
      let body = comment.trim() || `${rating}-star rating`;
      if (body.length < 10) body = `${body} · submitted via Central Rentals`;
      if (target?.user_id) {
        await api.post("/reviews", {
          target_user_id: target.user_id,
          target_role: target.role,
          rating,
          title: `${rating}-star review`,
          body,
          is_public: false,
        });
      } else {
        // No linked lease/contact yet — store with the Central Rentals team instead.
        await api.post("/feedback/review", {
          rating,
          comment: body,
          role: user?.role || "",
          name: user?.name || "",
          email: user?.email || "",
        });
      }
      setDone(true);
      toast.success("Thanks for your review!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit review");
    } finally { setSubmitting(false); }
  };

  if (loading) return <Loader/>;

  if (done) return (
    <div>
      <PageHeader title="Thank you" subtitle="Your feedback has been received."/>
      <Card className="p-12 text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 text-green-700 mx-auto flex items-center justify-center"><ThumbsUp className="w-8 h-8"/></div>
        <h2 className="font-display text-2xl font-bold mt-5 text-slate-900">Review submitted</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
          {target ? "It has been delivered to their feedback inbox." : "Our team will read it shortly."} We appreciate you taking the time.
        </p>
        {googleUrl && (
          <a href={googleUrl} target="_blank" rel="noreferrer" data-testid="google-review-link"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors">
            Love Central Rentals? Review us on Google <ExternalLink className="w-4 h-4"/>
          </a>
        )}
      </Card>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Leave a review"
        subtitle={isTenant
          ? "Help your landlord improve. Reviews are visible to your landlord only."
          : "Rate your experience or your tenant. Feedback stays private."}
      />

      <Card className="p-6 max-w-2xl">
        <form onSubmit={submit} className="space-y-5">
          <div>
            <div className="text-sm font-medium text-slate-700 mb-2">Your rating</div>
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => {
                const active = (hover || rating) >= n;
                return (
                  <button key={n} type="button" data-testid={`star-${n}`}
                    onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                    className={`p-1.5 rounded transition-colors ${active ? "text-amber-400" : "text-slate-300 hover:text-amber-300"}`}>
                    <Star className={`w-9 h-9 ${active ? "fill-current" : ""}`}/>
                  </button>
                );
              })}
              <span className="ml-3 text-sm text-slate-500">{rating > 0 ? "" : "Tap a star"}</span>
            </div>
          </div>

          <Textarea label="Comments" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What went well? Anything to improve?" testid="review-comment-input"/>

          <Button type="submit" disabled={submitting} data-testid="submit-review-button">{submitting ? "Sending…" : "Submit review"}</Button>
        </form>
      </Card>

      {googleUrl && (
        <p className="mt-4 text-sm text-slate-500">
          Prefer Google? <a href={googleUrl} target="_blank" rel="noreferrer" className="font-semibold text-green-800 hover:underline" data-testid="google-review-inline-link">Leave us a Google review →</a>
        </p>
      )}
    </div>
  );
}
